import * as React from "react"
import { Loader2 } from "lucide-react"

import { BrokerChoiceCard } from "@/components/Broker/BrokerChoiceCard"
import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { isCustomCsvBrokerName, resolveBrokerDisplayName } from "@/utils/customCsvAccountDisplay"
import {
  resolveConnectWizardGridBrokers,
  normalizeConnectBrokerSlug,
} from "@/views/Dashboard/Profile/connectBroker/connectBrokerWizardConstants"
import { isTradingViewBrandConnected } from "@/utils/tradingViewConnectBrand"

const SECTION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const SECTION_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white"

export type ConnectBrokerChooseStepProps = {
  brokers: BrokerCapabilities[]
  connectedBrokerSlugs: Set<string>
  selectedBrokerName: string | null
  onSelectBroker: (brokerName: string) => void
  loading?: boolean
  /** Override the popular-brokers section description (e.g. demo CSV-only funnel). */
  popularSectionDescription?: string
  /** Hide the “Popular brokers” heading + description (e.g. when the shell already has a page title). */
  hideSectionIntro?: boolean
}

export function ConnectBrokerChooseStep({
  brokers,
  connectedBrokerSlugs,
  selectedBrokerName,
  onSelectBroker,
  loading = false,
  popularSectionDescription,
  hideSectionIntro = false,
}: ConnectBrokerChooseStepProps): React.ReactElement {
  const gridBrokers = React.useMemo(
    () => resolveConnectWizardGridBrokers(brokers),
    [brokers]
  )

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <div className="flex w-full min-w-0 flex-col gap-4">
        {!hideSectionIntro ? (
          <div className="flex min-w-0 flex-col gap-1">
            <h3 className={SECTION_LABEL_CLASS}>Popular brokers</h3>
            <p className={SECTION_DESC_CLASS}>
              {popularSectionDescription ??
                "Select the broker you trade with. Tessera supports API sync and CSV import for the brokers below."}
            </p>
          </div>
        ) : null}

        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading brokers…
          </div>
        ) : gridBrokers.length === 0 ? (
          <p className="py-8 text-center font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
            No brokers available.
          </p>
        ) : (
          <div
            data-connect-broker-logo-grid=""
            className="grid w-full min-w-0 grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
            role="listbox"
            aria-label="Supported brokers"
          >
            {gridBrokers.map((broker) => {
              const slug = normalizeConnectBrokerSlug(broker.broker_name)
              const displayName = resolveBrokerDisplayName(broker)
              const isSelected = selectedBrokerName === broker.broker_name
              const isOtherBroker = isCustomCsvBrokerName(broker.broker_name)
              const isConnected =
                slug === "tradingview"
                  ? isTradingViewBrandConnected(connectedBrokerSlugs)
                  : connectedBrokerSlugs.has(slug)

              return (
                <BrokerChoiceCard
                  key={broker.broker_name}
                  brokerName={broker.broker_name}
                  displayName={displayName}
                  selected={isSelected}
                  connected={isConnected}
                  variant={isOtherBroker ? "other" : "logo"}
                  onSelect={() => onSelectBroker(broker.broker_name)}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
